import { useLocation } from '@reach/router';
import { graphql } from 'gatsby';
import React, { useMemo } from 'react';

import { IconPointCircle } from '../components/Icons';

import Layout from '../components/Layout';
import Toc from '../components/Toc';
import SEO from '../components/seo';

import SideBar from '../components/SideBar/SideBar';
import './tutorial.scss';
//import Feedback from '../components/Feedback/Feedback';
import { MDXRenderer } from 'gatsby-plugin-mdx';
import Breadcrumbs from '../components/Breadcrumbs/Breadcrumbs';
import MiscellaneousTutorialsBoxs from '../components/MiscellaneousTutorialsBox/MiscellaneousTutorialsBoxs';
import { LibraryItems } from '../entities/LibraryItem';
import {
  CLOUD_PAGE,
  CLOUD_PATH,
  EXAMPLES_PAGE,
  EXAMPLES_PATH,
  HARDWARE_PAGE,
  HARDWARE_PATH,
  LEARN_PAGE,
  LEARN_PATH,
  MICROPYTHON_PAGE,
  MYCROPYTHON_PATH,
  PROGRAMMING_PAGE,
  SIDEBAR_CLOUD,
  SIDEBAR_EXAMPLES,
  SIDEBAR_HARDWARE,
  SIDEBAR_IDE_SOFTWARE,
  SIDEBAR_LEARN,
  SIDEBAR_MYCROPYTHON,
  SIDEBAR_PLC_SOFTWARE,
  SIDEBAR_PROGRAMMING,
  SIDEBAR_SOFTWARE,
  SIDEBAR_TUTORIALS,
  SOFTWARE_IDE_PAGE,
  SOFTWARE_PAGE,
  SOFTWARE_PATH,
  SOFTWARE_PLC_PAGE,
  TUTORIALS_PAGE,
  TUTORIAL_COMMUNICATION,
  TUTORIAL_GENERIC,
  TUTORIAL_NANO_GENERIC,
  TUTORIAL_PROJECT,
} from '../utils/constants';
import {
  getActualPage,
  getMicropythonSideBarDataRefactored,
  getSoftwareSideBarDataRefactored,
  getTutorialHardwareSideBarDataRefactored,
  getTutorialSideBarDataRefactored,
} from '../utils/functions';
import useStaticWindowSize from '../hooks/useStaticWindowSize';

interface FileItems {
  fields: {
    gitLogLatestDate: string;
  };
  relativePath: string;
  childMdx: {
    body: string;
    tableOfContents: any;
    frontmatter: {
      title: string;
      description: string;
      author: string;
      libraries: Array<LibraryItems>;
      software: Array<string>;
      hardware: Array<string>;
      contributeURL: string;
    };
  };
}
interface ParentItems {
  fields: {
    slug: string;
  };
  childMdx: {
    frontmatter: {
      title: string;
      status: string;
    };
  };
  relativeDirectory: {
    length: number;
  };
}
interface TutorialProps {
  data: {
    file: FileItems;
    parent: ParentItems;
    families: {
      edges: Array<any>;
    };
    categories: {
      edges: Array<any>;
    };
    products: {
      edges: Array<any>;
    };
    pages: {
      edges: Array<any>;
    };
    micropythonPages: {
      edges: Array<any>;
    };
    softwarePages: {
      edges: Array<any>;
    };
    softwareSubpages: {
      edges: Array<any>;
    };
    learnPages: {
      edges: Array<any>;
    };
    examplesPages: {
      edges: Array<any>;
    };
    tutorials: {
      edges: Array<any>;
    };
  };
}

export default function Tutorial({ data }: TutorialProps): React.ReactElement {
  const { frontmatter, body, tableOfContents } = data.file.childMdx;
  const location = useLocation();
  const parts = location.pathname.split('/');
  //const isDesktop = typeof window !== 'undefined' ? window.innerWidth > 768 : true;
  const { width } = useStaticWindowSize();

  const isDesktop = useMemo(() => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth > 768) return true;
      return false;
    } else return true;
  }, []);

  //console.log('testDesktop', testDesktop);
  // const [domLoaded, setDomLoaded] = useState(false);

  // useEffect(() => {
  //   setDomLoaded(true);
  // }, []);
  const actualPage = getActualPage(location.pathname.split('/'));

  const sideBarData = useMemo(() => {
    if (actualPage.title === SIDEBAR_CLOUD) return getTutorialSideBarDataRefactored(data.pages);
    else if (actualPage.title === SIDEBAR_HARDWARE) return getTutorialHardwareSideBarDataRefactored(data.parent?.childMdx.frontmatter.title, data);
    else if (actualPage.title === SIDEBAR_MYCROPYTHON) return getMicropythonSideBarDataRefactored(data);
    else if (actualPage.title === SIDEBAR_SOFTWARE || actualPage.title === SIDEBAR_IDE_SOFTWARE || actualPage.title === SIDEBAR_PLC_SOFTWARE)
      return getSoftwareSideBarDataRefactored(data);
    else if (actualPage.title === SIDEBAR_LEARN) return getTutorialSideBarDataRefactored(data.learnPages);
    else if (actualPage.title === SIDEBAR_EXAMPLES) return getTutorialSideBarDataRefactored(data.examplesPages);
    else return null;
  }, [actualPage.title, data]);

  const sideBarTitle = useMemo(() => {
    if (parts[1] === SOFTWARE_PATH) return 'Software tools';
    if (parts[1] === MYCROPYTHON_PATH) return SIDEBAR_MYCROPYTHON;
    if (parts[1] === CLOUD_PATH) return SIDEBAR_CLOUD;
    if (parts[1] === EXAMPLES_PATH) return SIDEBAR_EXAMPLES;
    if (parts[1] === LEARN_PATH) return SIDEBAR_LEARN;
    if (
      parts[1] === HARDWARE_PATH &&
      (parts[2] === TUTORIAL_COMMUNICATION || parts[2] === TUTORIAL_GENERIC || parts[2] === TUTORIAL_PROJECT || parts[2] === TUTORIAL_NANO_GENERIC)
    )
      return SIDEBAR_TUTORIALS;
    else return 'Hardware';
  }, [parts]);

  const buildBreadcrumbs = useMemo(() => {
    if (actualPage.title === SIDEBAR_SOFTWARE) {
      return [
        { title: 'Home', url: '/' },
        { title: SIDEBAR_SOFTWARE, url: SOFTWARE_PAGE },
        { title: frontmatter.title, url: null },
      ];
    } else if (actualPage.title === SIDEBAR_IDE_SOFTWARE) {
      return [
        { title: 'Home', url: '/' },
        { title: SIDEBAR_SOFTWARE, url: SOFTWARE_PAGE },
        { title: SIDEBAR_IDE_SOFTWARE, url: SOFTWARE_IDE_PAGE },
        { title: frontmatter.title, url: null },
      ];
    } else if (actualPage.title === SIDEBAR_PLC_SOFTWARE) {
      return [
        { title: 'Home', url: '/' },
        { title: SIDEBAR_SOFTWARE, url: SOFTWARE_PAGE },
        { title: SIDEBAR_PLC_SOFTWARE, url: SOFTWARE_PLC_PAGE },
        { title: frontmatter.title, url: null },
      ];
    } else if (actualPage.title === SIDEBAR_MYCROPYTHON) {
      return [
        { title: 'Home', url: '/' },
        { title: SIDEBAR_PROGRAMMING, url: PROGRAMMING_PAGE },
        { title: SIDEBAR_MYCROPYTHON, url: MICROPYTHON_PAGE },
        { title: frontmatter.title, url: null },
      ];
    } else if (actualPage.title === SIDEBAR_LEARN) {
      return [
        { title: 'Home', url: '/' },
        { title: SIDEBAR_LEARN, url: LEARN_PAGE },
        { title: frontmatter.title, url: null },
      ];
    } else if (actualPage.title === SIDEBAR_EXAMPLES) {
      return [
        { title: 'Home', url: '/' },
        { title: SIDEBAR_PROGRAMMING, url: PROGRAMMING_PAGE },
        { title: SIDEBAR_EXAMPLES, url: EXAMPLES_PAGE },
        { title: frontmatter.title, url: null },
      ];
    } else if (actualPage.title === SIDEBAR_CLOUD) {
      return [
        { title: 'Home', url: '/' },
        { title: SIDEBAR_CLOUD, url: CLOUD_PAGE },
        { title: frontmatter.title, url: null },
      ];
    } else if (actualPage.title === SIDEBAR_TUTORIALS) {
      return [
        { title: 'Home', url: '/' },
        { title: SIDEBAR_TUTORIALS, url: TUTORIALS_PAGE },
        { title: frontmatter.title, url: null },
      ];
    } else {
      return [
        { title: 'Home', url: '/' },
        { title: SIDEBAR_HARDWARE, url: HARDWARE_PAGE },
        { title: data.parent?.childMdx.frontmatter.title ?? SIDEBAR_HARDWARE, url: data.parent?.fields.slug ?? HARDWARE_PAGE },
        { title: frontmatter.title, url: null },
      ];
    }
  }, [actualPage, data.parent?.childMdx.frontmatter.title, data.parent?.fields.slug, frontmatter.title]);

  return (
    <Layout>
      <SEO title={frontmatter.title} description={frontmatter.description} />
      <main className="page-container">
        <SideBar
          links={[]}
          nestedCategories={sideBarData}
          selectedItem={data.parent?.childMdx.frontmatter.title}
          actualPage={sideBarTitle}
          leafPage={frontmatter.title}
          prevPage={{ title: 'GO BACK', pathname: actualPage.url }}
          isTutorials={width < 1024}
        />
        <div className="page-layout tutorial">
          <div className="tutorial__container">
            <div className="tutorial__content">
              <Breadcrumbs actualPage={frontmatter.title} list={buildBreadcrumbs} />
              {['end-of-life', 'retired'].includes(data.parent?.childMdx.frontmatter?.status) && (
                <div className="tutorial__snackbar">
                  <svg preserveAspectRatio="false" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M12 0C9.62663 0 7.30655 0.703788 5.33316 2.02236C3.35977 3.34094 1.8217 5.21508 0.913451 7.4078C0.0051994 9.60051 -0.232441 12.0133 0.230582 14.3411C0.693605 16.6689 1.83649 18.807 3.51472 20.4853C5.19295 22.1635 7.33115 23.3064 9.65892 23.7694C11.9867 24.2324 14.3995 23.9948 16.5922 23.0865C18.7849 22.1783 20.6591 20.6402 21.9776 18.6668C23.2962 16.6934 24 14.3734 24 12C24 8.8174 22.7357 5.76515 20.4853 3.51472C18.2348 1.26428 15.1826 0 12 0V0ZM11.1429 4.28571C11.1429 4.05839 11.2332 3.84037 11.3939 3.67962C11.5547 3.51888 11.7727 3.42857 12 3.42857C12.2273 3.42857 12.4453 3.51888 12.6061 3.67962C12.7668 3.84037 12.8571 4.05839 12.8571 4.28571V14.5714C12.8571 14.7988 12.7668 15.0168 12.6061 15.1775C12.4453 15.3383 12.2273 15.4286 12 15.4286C11.7727 15.4286 11.5547 15.3383 11.3939 15.1775C11.2332 15.0168 11.1429 14.7988 11.1429 14.5714V4.28571ZM12 20.5714C11.7457 20.5714 11.4971 20.496 11.2857 20.3547C11.0743 20.2135 10.9095 20.0127 10.8122 19.7777C10.7148 19.5428 10.6894 19.2843 10.739 19.0349C10.7886 18.7855 10.9111 18.5564 11.0909 18.3766C11.2707 18.1968 11.4998 18.0743 11.7492 18.0247C11.9986 17.9751 12.2571 18.0006 12.492 18.0979C12.727 18.1952 12.9278 18.36 13.069 18.5714C13.2103 18.7828 13.2857 19.0314 13.2857 19.2857C13.2857 19.6267 13.1503 19.9537 12.9091 20.1948C12.668 20.436 12.341 20.5714 12 20.5714Z"
                      fill="black"
                    />
                  </svg>
                  <span>This tutorial refers to a product that has reached its end-of-life status.</span>
                </div>
              )}
              <h1>{frontmatter.title}</h1>
              <p>{frontmatter.description}</p>
              <div className="tutorial__metadata">
                {frontmatter.author && (
                  <div className="tutorial__metadata--author">
                    Author <IconPointCircle /> {frontmatter.author}
                  </div>
                )}
                {data.file.fields?.gitLogLatestDate && (
                  <div>
                    Last revision <IconPointCircle />
                    {new Date(data.file.fields?.gitLogLatestDate).toLocaleString([], {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    })}
                  </div>
                )}
              </div>
              <div className="tutorial__text">
                <MDXRenderer>{body}</MDXRenderer>
              </div>
              <MiscellaneousTutorialsBoxs />
            </div>
            <div className="tutorial__toc">{isDesktop && <Toc toc={tableOfContents}></Toc>}</div>
          </div>
          {/* <TutorialSupplies
            hardwareSupplies={frontmatter.hardware}
            softwareSupplies={frontmatter.software}
            librariesSupplies={frontmatter.libraries}
          /> */}
          {/* <Feedback smallMargin={true} /> */}
        </div>
      </main>
    </Layout>
  );
}

export const pageQuery = graphql`
  query ($slug: String!, $parent: String!, $product: String!) {
    file: file(fields: { slug: { eq: $slug } }) {
      fields {
        gitLogLatestDate
      }
      relativePath
      childMdx {
        body
        internal {
          contentFilePath
        }
        tableOfContents
        frontmatter {
          title
          description
          author
          libraries {
            name
            url
          }
          software
          hardware
          contributeURL
        }
      }
    }
    parent: file(fields: { slug: { eq: $parent } }) {
      fields {
        slug
      }
      relativeDirectory
      childMdx {
        frontmatter {
          title
          url_shop
          status
        }
      }
    }
    families: allFile(filter: { base: { eq: "family.md" } }, sort: { fields: { order: ASC } }) {
      edges {
        node {
          relativeDirectory
          childMdx {
            frontmatter {
              title
              description
              businessUnit
            }
          }
        }
      }
    }
    categories: allFile(filter: { base: { eq: "category.md" } }) {
      edges {
        node {
          relativeDirectory
          childMdx {
            frontmatter {
              title
            }
          }
        }
      }
    }
    products: allFile(filter: { base: { eq: "product.md" } }) {
      edges {
        node {
          relativeDirectory
          fields {
            slug
            category
          }
          childMdx {
            frontmatter {
              title
              core
            }
          }
        }
      }
    }
    pages: allFile(filter: { fields: { type: { eq: "arduino-cloud" } } }, sort: { relativeDirectory: ASC }) {
      edges {
        node {
          relativeDirectory
          name
          fields {
            slug
          }
          childMdx {
            frontmatter {
              title
              description
              icon
            }
            internal {
              contentFilePath
            }
          }
        }
      }
    }
    micropythonPages: allFile(filter: { fields: { type: { eq: "micropython" } } }, sort: { relativeDirectory: ASC }) {
      edges {
        node {
          relativeDirectory
          name
          fields {
            slug
          }
          childMdx {
            frontmatter {
              title
              description
              icon
            }
          }
        }
      }
    }
    softwarePages: allFile(filter: { fields: { type: { eq: "software" } } }, sort: { relativeDirectory: ASC }) {
      edges {
        node {
          relativeDirectory
          name
          fields {
            slug
          }
          childMdx {
            frontmatter {
              title
              description
              icon
            }
          }
        }
      }
    }
    softwareSubpages: allFile(filter: { fields: { type: { eq: "softwarepage" } } }, sort: { relativeDirectory: ASC }) {
      edges {
        node {
          relativeDirectory
          name
          fields {
            slug
          }
          childMdx {
            frontmatter {
              title
              description
              icon
            }
          }
        }
      }
    }
    learnPages: allFile(filter: { fields: { type: { eq: "learn" } } }, sort: { relativeDirectory: ASC }) {
      edges {
        node {
          relativeDirectory
          name
          fields {
            slug
          }
          childMdx {
            frontmatter {
              title
              description
              icon
            }
          }
        }
      }
    }
    examplesPages: allFile(filter: { fields: { type: { eq: "built-in-examples" } } }, sort: { relativeDirectory: ASC }) {
      edges {
        node {
          relativeDirectory
          name
          fields {
            slug
          }
          childMdx {
            frontmatter {
              title
              description
              icon
            }
          }
        }
      }
    }
    tutorials: allFile(filter: { fields: { product: { eq: $product } } }, sort: { relativeDirectory: ASC }) {
      edges {
        node {
          fields {
            slug
            gitLogLatestDate
          }
          childMdx {
            frontmatter {
              title
              description
              tags
              author
              difficulty
            }
          }
        }
      }
    }
  }
`;
